import {tx, ptx, usePageText} from '../../lib/page-text';
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api-client";
import { BoardShapeView } from "../board/BoardShape";
import { useBoardImages } from "../board/media";
import type { Shape } from "../board/model";
type Event = {
  id: string;
  kind: string;
  at: number;
  payload: {
    shapes?: Shape[];
    background?: string;
    mode?: string;
    title?: string;
  };
};
export function useReplayTimeline(
  streamId?: string,
  recordingId?: string,
  publicReplay?: { key: string; token?: string },
) {
  return useQuery({
    queryKey: [
      "replay-timeline",
      streamId,
      recordingId,
      publicReplay?.key,
      publicReplay?.token,
    ],
    enabled: !!(streamId || publicReplay?.key) && !!recordingId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<{ available: boolean; events: Event[]; reason?: string }>(
          publicReplay
            ? `/streams/${encodeURIComponent(publicReplay.key)}/program/replays/${recordingId}/timeline`
            : `/me/streams/${streamId}/program/timeline/${recordingId}`,
          { params: { t: publicReplay?.token } },
        )
      ).data,
  });
}
export function ReplayPresentation({
  events,
  time,
  streamId,
  publicReplay,
  recordingId,
  onSeek,
}: {
  events: Event[];
  time: number;
  streamId?: string;
  publicReplay?: { key: string; token?: string };
  recordingId: string;
  onSeek: (seconds: number) => void;
}) {
  usePageText();
  const board = events
    .filter((e) => e.kind === "board" && e.at <= time)
    .at(-1)?.payload;
  const media = useBoardImages(
    board?.shapes ?? [],
    publicReplay
      ? `/streams/${encodeURIComponent(publicReplay.key)}/program/replays/${recordingId}/timeline`
      : `/me/streams/${streamId}/program/timeline/${recordingId}`,
    publicReplay?.token,
  );
  const chapters = events.filter((e) => e.kind === "chapter");
  return (
    <div className="replay-presentation">
      {board && (
        <div className="replay-board">
          <svg
            role="img"
            aria-label={tx("Recorded presentation board")}
            viewBox="0 0 1000 600"
            style={{ width: "100%", display: "block", background: "#fff" }}
          >
            <defs>
              <pattern
                id="replay-grid"
                width="25"
                height="25"
                patternUnits="userSpaceOnUse"
              >
                <path d="M 25 0 L 0 0 0 25" fill="none" stroke="#e2e8f0" />
              </pattern>
              <pattern
                id="replay-dots"
                width="25"
                height="25"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="2" cy="2" r="1" fill="#cbd5e1" />
              </pattern>
            </defs>
            {board.background !== "plain" && (
              <rect
                width="1000"
                height="600"
                fill={`url(#replay-${board.background})`}
              />
            )}
            {board.shapes?.map((s) => (
              <BoardShapeView
                key={s.id}
                shape={s}
                image={s.imageId ? media.images[s.imageId] : undefined}
                prefix="replay"
              />
            ))}
          </svg>
          {media.failed && (
            <p role="alert">{ptx("Some board images could not load.{v0}{v1}", {v0: (" "), v1: (<button className="btn outline sm" onClick={media.retry}>{tx("Retry images")}</button>)})}</p>
          )}
        </div>
      )}
      {chapters.length > 0 && (
        <nav className="program-row" aria-label={tx("Replay chapters")}>
          {chapters.map((c) => (
            <button
              key={c.id}
              className="btn outline sm"
              onClick={() => onSeek(c.at)}
            >
              {Math.floor(c.at / 60)}:
              {String(Math.floor(c.at % 60)).padStart(2, "0")} ·{" "}
              {c.payload.title}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
